import { useState } from "react";
import { toast } from "sonner";
import { Plus, KeyRound, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { ListTableShell } from "@/components/ResourceShell";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { CreatePortalUserDialog } from "@/components/dialogs/CreatePortalUserDialog";
import { ChangePasswordDialog } from "@/components/dialogs/ChangePasswordDialog";
import { usePortalUsers } from "@/api/queries/portalUsers";
import { useDeletePortalUser } from "@/api/mutations/portalUsers";
import { relativeTime } from "@/lib/relativeTime";
import type { PortalUser } from "@/types";

export function AdminsPage() {
  const users = usePortalUsers();
  const del = useDeletePortalUser();
  const [createOpen, setCreateOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<PortalUser | null>(null);
  const [pwTarget, setPwTarget] = useState<PortalUser | null>(null);

  const all = users.data ?? [];

  function confirmDelete() {
    if (!deleteTarget) return;
    del.mutate(deleteTarget.id, {
      onSuccess: () => setDeleteTarget(null),
      onError: (e) => {
        // LAST_ADMIN u.a. → als Toast (der Dialog bleibt offen fuer erneuten Versuch/Abbruch).
        toast.error(e.message);
      },
    });
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="text-sm text-text3">{all.length} Portal-Admins</div>
        <Button onClick={() => setCreateOpen(true)} data-testid="create-admin-button">
          <Plus className="h-4 w-4" /> Admin anlegen
        </Button>
      </div>

      <ListTableShell
        isLoading={users.isLoading}
        isError={users.isError}
        errorMessage={users.error?.message}
        isEmpty={all.length === 0}
        emptyMessage="Keine Admins"
        onRetry={() => users.refetch()}
      >
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Email</TableHead>
              <TableHead>Rolle</TableHead>
              <TableHead>Angelegt</TableHead>
              <TableHead></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {all.map((u) => (
              <TableRow key={u.id}>
                <TableCell className="font-medium">{u.email}</TableCell>
                <TableCell className="text-text2">{u.role}</TableCell>
                <TableCell className="text-text3">{relativeTime(u.createdAt)}</TableCell>
                <TableCell className="text-right">
                  <div className="flex justify-end gap-1.5">
                    <Button variant="outline" size="sm" onClick={() => setPwTarget(u)} data-testid={`change-pw-${u.id}`}>
                      <KeyRound className="h-4 w-4" /> Passwort
                    </Button>
                    <Button variant="destructive" size="sm" onClick={() => setDeleteTarget(u)} data-testid={`delete-admin-${u.id}`}>
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </ListTableShell>

      {createOpen && <CreatePortalUserDialog open={createOpen} onOpenChange={setCreateOpen} />}

      {pwTarget && (
        <ChangePasswordDialog
          open={!!pwTarget}
          onOpenChange={(o) => !o && setPwTarget(null)}
          userId={pwTarget.id}
          userEmail={pwTarget.email}
        />
      )}

      {deleteTarget && (
        <ConfirmDialog
          open={!!deleteTarget}
          onOpenChange={(o) => !o && setDeleteTarget(null)}
          title="Admin loeschen"
          description={`${deleteTarget.email} wird geloescht. Der letzte Admin ist nicht loeschbar.`}
          confirmText="Loeschen"
          confirmVariant="destructive"
          isPending={del.isPending}
          onConfirm={confirmDelete}
        />
      )}
    </div>
  );
}
